import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { paymentStatus } from '../lib/api';

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | success | cancel | error
  const attempts = useRef(0);

  const isCancel = location.pathname.includes('cancel');

  useEffect(() => {
    if (isCancel) { setState('cancel'); return; }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) { setState('error'); return; }

    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setState('error'); return; }
      attempts.current += 1;
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === 'paid') { setState('success'); return; }
        if (res.status === 'expired' || res.payment_status === 'failed') { setState('error'); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [location, isCancel]);

  return (
    <div className="max-w-xl mx-auto px-5 pt-20 pb-10 text-center">
      <div className="bg-white rounded-[24px] p-10 shadow-[0_30px_80px_-45px_rgba(14,23,38,0.4)]">
        {state === 'checking' && (
          <>
            <Loader2 size={44} className="mx-auto text-[#2E6BFF] animate-spin" />
            <h1 className="font-display font-extrabold text-[#0E1726] text-[26px] mt-5">Confirming your payment...</h1>
            <p className="text-[#5B6472] mt-2">This only takes a moment.</p>
          </>
        )}
        {state === 'success' && (
          <>
            <CheckCircle2 size={52} className="mx-auto text-[#10B981]" />
            <h1 className="font-display font-extrabold text-[#0E1726] text-[28px] mt-5">You're enrolled!</h1>
            <p className="text-[#5B6472] mt-3">Welcome to the AI for PMs cohort. We'll email you the schedule and next steps shortly.</p>
            <Link to="/community" className="inline-block mt-6 bg-[#2E6BFF] text-white font-semibold px-6 py-3 rounded-full">Go to community</Link>
          </>
        )}
        {state === 'cancel' && (
          <>
            <XCircle size={52} className="mx-auto text-[#F59E0B]" />
            <h1 className="font-display font-extrabold text-[#0E1726] text-[28px] mt-5">Checkout canceled</h1>
            <p className="text-[#5B6472] mt-3">No charge was made. You can enroll whenever you're ready.</p>
            <button onClick={() => navigate('/enroll')} className="mt-6 bg-[#7C3AED] text-white font-semibold px-6 py-3 rounded-full">Back to enrollment</button>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle size={52} className="mx-auto text-[#EF4444]" />
            <h1 className="font-display font-extrabold text-[#0E1726] text-[28px] mt-5">We couldn't confirm the payment</h1>
            <p className="text-[#5B6472] mt-3">If you were charged, please contact us and we'll sort it out right away.</p>
            <Link to="/contact" className="inline-block mt-6 bg-[#0E1B2E] text-white font-semibold px-6 py-3 rounded-full">Contact support</Link>
          </>
        )}
      </div>
    </div>
  );
}
